import { FC, Fragment, useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useGalleryAssignment, useGalleryVisuals, useGalleryVisualsMeta } from '../../_main/contexts'

import BlockInfo from 'components/common/BlockInfo/BlockInfo'
import Button from 'components/common/Button/Button'
import { InputWrap } from 'components/common/InputWrap'
import Modal from 'components/common/Modals/Modal/Modal'
import { Nullable } from 'models/helpers'
import { QueryStatus } from 'components/common/QueryStatus'
import { UseMutationResult } from '@tanstack/react-query'
import styles from './SubmitAssignmentModal.module.sass'
import { useAuth0 } from 'utils/auth'

export type SubmitAssignmentModalType = 'visual' | 'document'
interface Props {
  type: SubmitAssignmentModalType
  isOpen: boolean
  mutation: UseMutationResult<any, any, any, any>
  /** null leaves the saved comment untouched; only the visual submit skips it, the document submit wipes on null */
  onSubmit: (creativeNote: Nullable<string>) => void
  onClose: () => void
  onAfterClosed: () => void
}

/**
 * Displays modal for finishing assignment submission.
 * Textarea for optional CT note for BKBN is present.
 * 
 * @example
 * <SubmitAssignmentModal
 *  isOpen={false}
 *  onSubmit={(creativeNote) => console.log(creativeNote)}
 *  onClose={() => alert('I've used to be an adventurer like you')}
 *  onAfterClosed={() => alert('But then i took an arrow into knee')}
 * />
 */
export const SubmitAssignmentModal: FC<Props> = ({
  isOpen,
  type,
  mutation,
  onSubmit,
  onClose,
  onAfterClosed,
}) => {

  const { t } = useTranslation(['submit_assignment', 'gallery'])

  const { roles } = useAuth0()

  const {
    uploadMinCount,
    uploadMaxCount,
  } = useGalleryVisualsMeta()

  const {
    visualsCount,
  } = useGalleryVisuals()

  const { assignment, assignmentData } = useGalleryAssignment()

  // undefined until the box is edited, so a comment the creative saved earlier is shown and sent back instead of wiped
  const [editedNote, setEditedNote] = useState<string>()
  const savedNote = assignmentData && 'creativeCommentsForAdmin' in assignmentData ? assignmentData.creativeCommentsForAdmin : undefined
  const creativeNote = editedNote ?? savedNote ?? ''

  const refetchAssignment = assignment.refetch
  useEffect(() => {
    if (isOpen) refetchAssignment()
  }, [isOpen, refetchAssignment])

  const handleClose = useCallback(() => {
    setEditedNote(undefined)
    onClose()
  }, [onClose])

  return (
    <Modal
      isOpen={isOpen}
      modalContentClassName={styles.modalBody}
      afterClosed={onAfterClosed}
      title={t('submit_assignment')}
    >

      <Trans t={t} i18nKey="info" parent="p">
        &nbsp;
      </Trans>

      <div className={styles.inputRow}>
        <label htmlFor="creativeNote">
          {t('note_label')}
        </label>
        <InputWrap label=''>
          <textarea
            rows={5}
            name="creativeNote"
            value={creativeNote}
            disabled={assignment.isFetching}
            onChange={e => setEditedNote(e.target.value)}
          ></textarea>
        </InputWrap>
      </div>

      <div className={styles.footer}>

        <QueryStatus
          query={assignment.isError ? assignment : mutation}
          successMessage={t('success')}
          spaceBottomRem={2}
          spaceTopRem={2}
        />

        {type === 'visual' &&
          <Fragment>

            {roles.isCreative && visualsCount < uploadMinCount &&
              <BlockInfo className={styles.warning}>
                <Trans
                  t={t}
                  i18nKey="gallery:min_upload_limit_hint"
                  values={{ count: uploadMinCount }}
                />
              </BlockInfo>
            }

            {roles.isCreative && visualsCount > uploadMaxCount &&
              <BlockInfo className={styles.warning}>
                <Trans
                  t={t}
                  i18nKey="gallery:max_upload_limit_hint"
                  values={{ count: uploadMaxCount }}
                />
              </BlockInfo>
            }

          </Fragment>
        }

        <div className={styles.actionRow}>

          <Button
            type="secondary nobackground"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {t(!mutation.isSuccess ? 'cancel' : 'close')}
          </Button>

          {!mutation.isSuccess &&
            <Button
              onClick={_ => onSubmit(type === 'visual' ? editedNote ?? null : creativeNote)}
              disabled={mutation.isPending || assignment.isFetching || assignment.isError}
            >
              {t('submit_assignment')}
            </Button>
          }

        </div>

      </div>

    </Modal>
  )
}
